import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  TextField,
} from '@mui/material';

type AddEffectRegistrationTimeOptionDialogProps = {
  open: boolean;
  existingOffsets: number[];
  isSaving: boolean;
  onClose: () => void;
  onAdd: (minutes: number, enableOnExistingConfigs: boolean) => void;
};

const AddEffectRegistrationTimeOptionDialog: React.FC<AddEffectRegistrationTimeOptionDialogProps> = (props) => {
  const { open, existingOffsets, isSaving, onClose, onAdd } = props;
  const [hoursValue, setHoursValue] = useState('');
  const [minutesValue, setMinutesValue] = useState('');
  const [shouldBeActiveOnModifiedChainConfigs, setShouldBeActiveOnModifiedChainConfigs] = useState(true);

  const resetFields = () => {
    setHoursValue('');
    setMinutesValue('');
    setShouldBeActiveOnModifiedChainConfigs(true);
  };

  const hours = hoursValue === '' ? 0 : Number(hoursValue);
  const restMinutes = minutesValue === '' ? 0 : Number(minutesValue);
  const minutes = hours * 60 + restMinutes;

  let hoursError: string | undefined;
  let minutesError: string | undefined;
  let totalError: string | undefined;

  if (!Number.isInteger(hours) || hours < 0 || hours > 23) hoursError = 'Enter a whole number of hours (0-23)';

  if (!Number.isInteger(restMinutes) || restMinutes < 0 || restMinutes > 59) minutesError = 'Enter 0-59 minutes';

  const isTouched = hoursValue !== '' || minutesValue !== '';

  if (!hoursError && !minutesError && isTouched) {
    if (minutes <= 0) totalError = 'The offset must be greater than 0';
    else if (existingOffsets.includes(minutes)) totalError = 'This option already exists';
  }
  const isValid = isTouched && !hoursError && !minutesError && !totalError;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" slotProps={{ transition: { onExited: resetFields } }}>
      <DialogTitle>Add evaluation time option</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            type="number"
            slotProps={{
              htmlInput: { min: 0, inputMode: 'numeric' },
              input: { endAdornment: <InputAdornment position="end">Hours</InputAdornment> },
            }}
            value={hoursValue}
            onChange={(e) => setHoursValue(e.target.value)}
            error={!!hoursError || !!totalError}
            helperText={hoursError}
          />
          <TextField
            fullWidth
            margin="dense"
            type="number"
            slotProps={{
              htmlInput: { min: 0, inputMode: 'numeric' },
              input: { endAdornment: <InputAdornment position="end">Minutes</InputAdornment> },
            }}
            value={minutesValue}
            onChange={(e) => setMinutesValue(e.target.value)}
            error={!!minutesError || !!totalError}
            helperText={minutesError}
          />
        </Box>
        {totalError && <FormHelperText error>{totalError}</FormHelperText>}
        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Checkbox
              checked={shouldBeActiveOnModifiedChainConfigs}
              onChange={(e) => setShouldBeActiveOnModifiedChainConfigs(e.target.checked)}
            />
          }
          label="Also activate on PN prescription chains where the time options have been customised"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          disabled={!open || !isValid || isSaving}
          onClick={() => onAdd(minutes, shouldBeActiveOnModifiedChainConfigs)}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEffectRegistrationTimeOptionDialog;
